'use client'

import React, { useCallback, useMemo, useRef } from 'react'

const FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export const getFocusableElementsInOverlay = (container: HTMLElement): HTMLElement[] =>
    Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null && !el.hasAttribute('aria-hidden'),
    )

/** Maps any integer index into `[0, length)` with wrap; fixes JS `%` for negatives (menu roving focus). */
const wrapFocusIndex = (index: number, length: number): number => ((index % length) + length) % length

/** Shared props for components that use the accessible overlay pattern (popover, dialog, tooltip, etc.). */
export interface AccessibleOverlayProps {
    /** Accessible label for the overlay content when there is no visible heading. */
    ariaLabel?: string
    /** Id of the element that labels the overlay content. Use when content has a visible heading. */
    ariaLabelledBy?: string
    /** When true (default), Tab/Shift+Tab keeps focus inside the overlay. Set false to allow focus to leave. */
    trapFocus?: boolean
    /** ARIA role for the content container. Default 'dialog' for popovers. */
    role?: 'dialog' | 'region' | 'menu'
    /** Whether the overlay is modal (focus trap and inert outside). Default false for non-modal popovers. */
    ariaModal?: boolean
}

export interface UseAccessibleOverlayOptions extends AccessibleOverlayProps {
    isOpen: boolean
    contentId: string
}

export interface UseAccessibleOverlayReturn {
    triggerRef: React.MutableRefObject<HTMLElement | null>
    contentRef: React.MutableRefObject<HTMLDivElement | null>
    triggerAriaProps: {
        'aria-haspopup': 'dialog' | 'menu'
        'aria-expanded': boolean
        'aria-controls': string
    }
    contentProps: {
        id: string
        role: string
        'aria-modal': boolean
        tabIndex: number
        'aria-label'?: string
        'aria-labelledby'?: string
        onKeyDown: ((e: React.KeyboardEvent<HTMLDivElement>) => void) | undefined
    }
    focusTrigger: () => void
    focusContent: () => void
}

/**
 * Hook that wires up ARIA attributes, focus management, and optional focus trapping
 * for overlay-style components such as popovers, dialogs, and tooltips.
 */
export function useAccessibleOverlay({
    isOpen,
    contentId,
    trapFocus = true,
    ariaLabel,
    ariaLabelledBy,
    role = 'dialog',
    ariaModal = false,
}: UseAccessibleOverlayOptions): UseAccessibleOverlayReturn {
    const triggerRef = useRef<HTMLElement | null>(null)
    const contentRef = useRef<HTMLDivElement | null>(null)

    const focusTrigger = useCallback(() => {
        triggerRef.current?.focus()
    }, [])

    const focusContent = useCallback(() => {
        requestAnimationFrame(() => contentRef.current?.focus())
    }, [])

    const handleContentKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Tab' || !contentRef.current) return
        const focusables = getFocusableElementsInOverlay(contentRef.current)
        if (focusables.length === 0) return
        const currentIndex = focusables.indexOf(document.activeElement as HTMLElement)
        const lastIndex = focusables.length - 1
        if (e.shiftKey) {
            if (currentIndex <= 0 || currentIndex === -1) {
                e.preventDefault()
                focusables[lastIndex].focus()
            }
        } else if (currentIndex >= lastIndex || currentIndex === -1) {
            e.preventDefault()
            focusables[0].focus()
        }
    }, [])

    const handleMenuKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!contentRef.current) return
        const focusables = getFocusableElementsInOverlay(contentRef.current)
        if (focusables.length === 0) return
        const currentIndex = focusables.indexOf(document.activeElement as HTMLElement)

        const count = focusables.length

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                focusables[wrapFocusIndex(currentIndex + 1, count)].focus()
                break
            case 'ArrowUp':
                e.preventDefault()
                focusables[wrapFocusIndex(currentIndex - 1, count)].focus()
                break
            case 'Home':
                e.preventDefault()
                focusables[0].focus()
                break
            case 'End':
                e.preventDefault()
                focusables[focusables.length - 1].focus()
                break
            default:
                break
        }
    }, [])

    const onKeyDown = useMemo(() => {
        if (role === 'menu') {
            return handleMenuKeyDown
        }
        if (trapFocus) {
            return handleContentKeyDown
        }
        return undefined
    }, [role, trapFocus, handleMenuKeyDown, handleContentKeyDown])

    return {
        triggerRef,
        contentRef,
        triggerAriaProps: {
            'aria-haspopup': role === 'menu' ? 'menu' : 'dialog',
            'aria-expanded': isOpen,
            'aria-controls': contentId,
        },
        contentProps: {
            id: contentId,
            role,
            'aria-modal': ariaModal,
            tabIndex: -1,
            'aria-label': ariaLabelledBy ? undefined : ariaLabel,
            'aria-labelledby': ariaLabelledBy,
            onKeyDown,
        },
        focusTrigger,
        focusContent,
    }
}
